import { AdPlacement } from "@/components/ads/AdPlacement";
import { m } from "@/paraglide/messages";
import { CalcBreadcrumb } from "../components/CalcBreadcrumb";
import { CalcExplainer } from "../components/CalcExplainer";
import { PercentCalc, type PercentCalcConfig } from "../components/PercentCalc";
import { RelatedCalculators } from "../components/RelatedCalculators";
import { findWhole, percentOf, whatPercent } from "../lib/percentage";

/** The three classic percentage questions, each its own mini calculator. */
const MODES: Array<{ title: () => string; config: PercentCalcConfig }> = [
  {
    title: () => m.pct_hub_mode1_title(),
    config: {
      fields: [
        { id: "percent", label: () => m.pct_field_percent() },
        { id: "value", label: () => m.pct_field_value() },
      ],
      compute: (v) => {
        const r = percentOf(v.percent, v.value);
        return r === null
          ? null
          : [{ label: () => m.pct_result_answer(), value: r, kind: "number" }];
      },
    },
  },
  {
    title: () => m.pct_hub_mode2_title(),
    config: {
      fields: [
        { id: "part", label: () => m.pct_field_part() },
        { id: "whole", label: () => m.pct_field_whole() },
      ],
      compute: (v) => {
        const r = whatPercent(v.part, v.whole);
        return r === null
          ? null
          : [{ label: () => m.pct_result_answer(), value: r, kind: "percent" }];
      },
    },
  },
  {
    title: () => m.pct_hub_mode3_title(),
    config: {
      fields: [
        { id: "amount", label: () => m.pct_field_amount() },
        { id: "percent", label: () => m.pct_field_percent() },
      ],
      compute: (v) => {
        const r = findWhole(v.amount, v.percent);
        return r === null
          ? null
          : [{ label: () => m.pct_result_answer(), value: r, kind: "number" }];
      },
    },
  },
];

export function PercentageCalculatorIndex() {
  return (
    <div className="pct-page relative min-h-screen">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-96 pct-glow" />
      <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <CalcBreadcrumb />

        <header className="mb-8 mt-6">
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {m.pct_hub_h1()}
          </h1>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            {m.pct_hub_intro()}
          </p>
        </header>

        <div className="space-y-6">
          {MODES.map((mode) => (
            <section key={mode.title()}>
              <h2 className="mb-3 font-display text-lg font-semibold tracking-tight">
                {mode.title()}
              </h2>
              <PercentCalc config={mode.config} />
            </section>
          ))}
        </div>

        <CalcExplainer
          howTo={m.pct_hub_howto()}
          examples={[
            m.pct_hub_ex1(),
            m.pct_hub_ex2(),
            m.pct_hub_ex3(),
            m.pct_hub_ex4(),
          ]}
          faqs={[
            { q: m.pct_hub_faq1_q(), a: m.pct_hub_faq1_a() },
            { q: m.pct_hub_faq2_q(), a: m.pct_hub_faq2_a() },
            { q: m.pct_hub_faq3_q(), a: m.pct_hub_faq3_a() },
            { q: m.pct_hub_faq4_q(), a: m.pct_hub_faq4_a() },
          ]}
        />

        <AdPlacement className="mt-10" />
        <RelatedCalculators />
      </div>
    </div>
  );
}
